import React from "react";
import { buildReviewIds, useReviewForm } from "../lib/useReviewForm";
import { slugify } from "../../../shared/lib/slugify";
import AdminPageLoader from "../../../shared/ui/admin-loader/AdminPageLoader";
import ImageUpload from "../../../shared/ui/image-upload/ImageUpload";
import { useAutoTranslate } from "../../../shared/lib/useAutoTranslate";
import TranslateButton from "../../../shared/ui/translate-button/TranslateButton";

const ReviewForm = ({ type, initialData, isEditMode }) => {
  const formik = useReviewForm(type, initialData, isEditMode);
  const { translateFields, isTranslating } = useAutoTranslate(formik);

  const handleTranslate = async () => {
    await translateFields([
      { from: 'quote.ua', to: 'quote.en' },
      { from: 'media.alt.ua', to: 'media.alt.en' },
    ]);
  };

  // Поки відгук створюють, ID і адреса підставляються з імені автора
  // та початку цитати; в режимі редагування їх не чіпаємо, щоб не рвати
  // посилання на вже збережений відгук.
  const authorName = formik.values.author?.name || "";
  const quoteText = formik.values.quote?.ua || formik.values.quote?.en || "";

  React.useEffect(() => {
    if (isEditMode) return;

    const { id, slug } = buildReviewIds(authorName, quoteText);

    if (id && formik.values.id !== id) formik.setFieldValue("id", id);
    if (slug && formik.values.slug !== slug) formik.setFieldValue("slug", slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorName, quoteText, isEditMode]);

  return (
    <form id="review-create-form" onSubmit={formik.handleSubmit} className="programs-create-form">
      {formik.isSubmitting && (
        <AdminPageLoader label="Збереження відгуку..." fullScreen />
      )}

      {/* Translate Button */}
      <div className="form-group" style={{ marginBottom: '24px' }}>
        <TranslateButton
          onClick={handleTranslate}
          isLoading={isTranslating}
          label="Перекласти УА → EN"
        />
      </div>

      {/* ID і адреса */}
      <div className="form-group">
        <label>ID відгуку *</label>
        <input
          type="text"
          value={formik.values.id || ""}
          onChange={(e) => formik.setFieldValue("id", e.target.value)}
          onBlur={formik.handleBlur}
          placeholder="review-name-course"
          style={{
            width: "100%",
            padding: "10px",
            border: `1px solid ${formik.errors.id ? "#FCA5A5" : "#ccc"}`,
            borderRadius: "6px",
          }}
        />
        <p style={{ fontSize: "12px", color: "#94A3B8", margin: "6px 0 0" }}>
          {isEditMode
            ? "Технічний ідентифікатор збереженого відгуку."
            : "Підставляється з імені автора й початку цитати — можна виправити вручну."}
        </p>
        {formik.errors.id && (
          <span style={{ color: "red", fontSize: "12px" }}>{formik.errors.id}</span>
        )}
      </div>

      <div className="form-group">
        <label>Slug (адреса) *</label>
        <input
          type="text"
          value={formik.values.slug || ""}
          onChange={(e) => formik.setFieldValue("slug", slugify(e.target.value))}
          onBlur={formik.handleBlur}
          placeholder="name-course"
          style={{
            width: "100%",
            padding: "10px",
            border: `1px solid ${formik.errors.slug ? "#FCA5A5" : "#ccc"}`,
            borderRadius: "6px",
          }}
        />
        {formik.errors.slug && (
          <span style={{ color: "red", fontSize: "12px" }}>{formik.errors.slug}</span>
        )}
      </div>

      {/* Quote (Локализованный) */}
      <div className="form-group">
        <label>Цитата відгуку *</label>
        <div className="form-wrapper-2-column">
          <div>
            <label style={{ fontSize: "12px", color: "#666" }}>Українська (UA)</label>
            <textarea
              value={formik.values.quote?.ua || ""}
              onChange={(e) => formik.setFieldValue("quote.ua", e.target.value)}
              onBlur={formik.handleBlur}
              placeholder="Текст відгуку українською..."
              className="custom-textarea"
              rows={4}
            />
            {formik.touched.quote?.ua && formik.errors.quote?.ua && (
              <span style={{ color: "red", fontSize: "12px" }}>{formik.errors.quote?.ua}</span>
            )}
          </div>
          <div>
            <label style={{ fontSize: "12px", color: "#666" }}>English (EN)</label>
            <textarea
              value={formik.values.quote?.en || ""}
              onChange={(e) => formik.setFieldValue("quote.en", e.target.value)}
              onBlur={formik.handleBlur}
              placeholder="Review text in English..."
              className="custom-textarea"
              rows={4}
            />
          </div>
        </div>
      </div>

      {/* Author Info */}
      <div className="form-group">
        <label>Дані автора</label>
        <div className="form-wrapper-2-column">
          <input
            type="text"
            value={formik.values.author?.name || ""}
            onChange={(e) => formik.setFieldValue("author.name", e.target.value)}
            placeholder="Ім'я автора"
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "6px",
            }}
          />
          <input
            type="text"
            value={formik.values.author?.subtitle || ""}
            onChange={(e) => formik.setFieldValue("author.subtitle", e.target.value)}
            placeholder="Посада, місто (IT Lead, Berlin)"
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "6px",
            }}
          />
        </div>
      </div>

      {/* Author Avatar */}
      <div className="form-group">
        <label>Аватар автора</label>
        <ImageUpload
          value={formik.values.author?.avatarUrl}
          onChange={(url) => formik.setFieldValue("author.avatarUrl", url)}
          uploadPath="reviews/avatars"
        />
      </div>

      {/* Media Upload - Video Only */}
      <div className="form-group">
        <label>Завантажити відео</label>
        <ImageUpload
          value={formik.values.media?.url}
          onChange={(url) => formik.setFieldValue("media.url", url)}
          label="Відео файл"
          accept="video/*"
        />
      </div>

      {/* Media Thumbnail - Video Poster */}
      <div className="form-group">
        <label>Постер для відео (превью)</label>
        <ImageUpload
          value={formik.values.media?.thumbnailUrl}
          onChange={(url) => formik.setFieldValue("media.thumbnailUrl", url)}
          label="Постер відео"
          accept="image/*"
        />
      </div>

      {/* Media Alt Text (Локализованный) */}
      <div className="form-group">
        <label>Альтернативний текст (Alt)</label>
        <div className="form-wrapper-2-column">
          <input
            type="text"
            value={formik.values.media?.alt?.ua || ""}
            onChange={(e) => formik.setFieldValue("media.alt.ua", e.target.value)}
            placeholder="Опис фото українською"
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "6px",
            }}
          />
          <input
            type="text"
            value={formik.values.media?.alt?.en || ""}
            onChange={(e) => formik.setFieldValue("media.alt.en", e.target.value)}
            placeholder="Photo description in English"
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "6px",
            }}
          />
        </div>
      </div>


      {/* Помилки, через які форма не зберігається */}
      {formik.submitCount > 0 && Object.keys(formik.errors).length > 0 && (
        <div
          style={{
            padding: "14px 16px",
            background: "#FEF2F2",
            border: "1px solid #FCA5A5",
            borderRadius: "8px",
            fontSize: "13px",
            color: "#B91C1C",
          }}
        >
          <strong>Не збережено — заповніть:</strong>
          <ul style={{ margin: "8px 0 0", paddingLeft: "20px" }}>
            {Object.entries(formik.errors).map(([key, value]) => (
              <li key={key}>
                {key}: {typeof value === "string" ? value : JSON.stringify(value)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Featured & Active Checkboxes */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <div className="form-group checkbox-group">
          <label htmlFor="isFeatured" className="checkbox-label">
            <input
              type="checkbox"
              id="isFeatured"
              checked={formik.values.isFeatured || false}
              onChange={(e) => formik.setFieldValue("isFeatured", e.target.checked)}
            />
            <span>Виділений відгук</span>
          </label>
        </div>

        <div className="form-group checkbox-group">
          <label htmlFor="isActive" className="checkbox-label">
            <input
              type="checkbox"
              id="isActive"
              checked={formik.values.isActive !== false}
              onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
            />
            <span>Активний</span>
          </label>
        </div>
      </div>
    </form>
  );
};

export default ReviewForm;
